import React from 'react';
import { Provider } from 'react-redux';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import './i18n/i18n.ts';
import history from './core/customHistory.ts';
import { configureStore } from './app/store/index.ts';
import AppContainer from './app/AppContainer.tsx';
import Snackbar from './app/components/snackbar/Snackbar.tsx';
import WebsocketProvider from './app/GlobalContexts/Websocket/WebsocketProvider.tsx';
import NotificationHubProvider from './app/GlobalContexts/NotificationHub/NotificationHubProvider.tsx';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import AppUnauthenticated from './app/AppUnauthenticated.tsx';
import VersionChecker from './app/components/VersionChecker/index.ts';
import TokenRefreshManager from './app/components/Auth/TokenRefreshManager.tsx';
import { useAuthTelemetry } from './app/hooks/useAuthTelemetry.ts';
import { lazyWithRetry } from './app/utils/lazyWithRetry.ts';

// Full-screen station windows render outside the Header/sidebar shell — see below.
const SimulationStationsApp = lazyWithRetry(
  () => import('./app/components/SimulationStations/SimulationStationsApp.tsx')
);

// prepare store
const [store] = configureStore();

/**
 * Main application component
 * Using type assertions to bypass React 18 type compatibility issues with older versions
 * of react-redux and react-router-dom
 */
export const Main = () => {
  // Initialize authentication telemetry
  useAuthTelemetry();

  // Create the JSX elements
  const authenticatedContent = (
    <AuthenticatedTemplate>
      <TokenRefreshManager />
      <NotificationHubProvider>
        <WebsocketProvider>
          <Switch>
            <Redirect from="/loginExternal" to="/" />
            {/* Full-screen station windows — matched before the AppContainer
                catch-all so they render without the Header/navigation chrome,
                while staying inside MSAL auth + the providers above. */}
            <Route
              path="/simulation/stations"
              render={() => (
                <React.Suspense fallback={null}>
                  <SimulationStationsApp />
                </React.Suspense>
              )}
            />
            <Route component={AppContainer} />
          </Switch>
          <Snackbar />
          <VersionChecker />
        </WebsocketProvider>
      </NotificationHubProvider>
    </AuthenticatedTemplate>
  );

  const unauthenticatedContent = (
    <UnauthenticatedTemplate>
      <Switch>
        <Route path="/loginExternal" component={AppUnauthenticated} />
        <Route path="*" component={AppUnauthenticated} />
      </Switch>
      <VersionChecker />
    </UnauthenticatedTemplate>
  );

  // Use type assertions to bypass React 18 type checking for older component libraries
  const ProviderComponent = Provider as any;
  const RouterComponent = Router as any;

  return (
    <ProviderComponent store={store}>
      <RouterComponent history={history}>
        {authenticatedContent}
        {unauthenticatedContent}
      </RouterComponent>
    </ProviderComponent>
  );
};

export default Main;
